<template>
  <div class="page-consultant-list">
    <div v-if="consultantsPage" class="vx-card p-6">
      <div class="flex flex-wrap items-center">
        <!-- ITEMS PER PAGE -->
        <div class="flex-grow">
          <vs-dropdown vs-trigger-click class="cursor-pointer">
            <div
              class="p-4 border border-solid d-theme-border-grey-light rounded-full d-theme-dark-bg cursor-pointer flex items-center justify-between font-medium"
            >
              <span class="mr-2">
                {{
                  currentPage * paginationPageSize - (paginationPageSize - 1)
                }}
                -
                {{ currentPage * paginationPageSize }}
                von {{ consultantsPage.total }}
              </span>
              <feather-icon icon="chevron-down" svg-classes="h-4 w-4" />
            </div>
            <vs-dropdown-menu>
              <vs-dropdown-item @click="gridApi.paginationSetPageSize(15)">
                <span>15</span>
              </vs-dropdown-item>
              <vs-dropdown-item @click="gridApi.paginationSetPageSize(20)">
                <span>20</span>
              </vs-dropdown-item>
              <vs-dropdown-item @click="gridApi.paginationSetPageSize(25)">
                <span>25</span>
              </vs-dropdown-item>
              <vs-dropdown-item @click="gridApi.paginationSetPageSize(30)">
                <span>30</span>
              </vs-dropdown-item>
            </vs-dropdown-menu>
          </vs-dropdown>
        </div>

        <!-- TABLE ACTION COL-2: SEARCH & EXPORT AS CSV -->
        <DataInput
          v-model="searchQuery"
          type="text"
          placeholder="Suche..."
          class="sm:mr-4 mr-0 sm:w-auto w-full sm:order-normal order-3 sm:mt-0 mt-4"
          name="searchQuery"
        />
        <!-- <vs-button class="mb-4 md:mb-0" @click="gridApi.exportDataAsCsv()">Export as CSV</vs-button> -->

        <!-- ACTION - DROPDOWN -->
        <vs-dropdown vs-trigger-click class="cursor-pointer">
          <div
            class="p-3 shadow-drop rounded-lg d-theme-dark-light-bg cursor-pointer flex items-end justify-center text-lg font-medium w-32"
          >
            <span class="mr-2 leading-none">Aktionen</span>
            <feather-icon icon="chevron-down" svg-classes="h-4 w-4" />
          </div>

          <vs-dropdown-menu>
            <vs-dropdown-item>
              <span class="flex items-center">
                <feather-icon icon="trash" svg-classes="h-4 w-4" class="mr-2" />
                <span>Löschen</span>
              </span>
            </vs-dropdown-item>

            <vs-dropdown-item>
              <span class="flex items-center">
                <feather-icon
                  icon="archive"
                  svg-classes="h-4 w-4"
                  class="mr-2"
                />
                <span>Archivieren</span>
              </span>
            </vs-dropdown-item>
          </vs-dropdown-menu>
        </vs-dropdown>
      </div>

      <!-- AgGrid Table -->
      <ag-grid-vue
        ref="agGridTable"
        :components="components"
        class="ag-theme-material w-100 my-4 ag-grid-table"
        :column-defs="columnDefs"
        :default-col-def="defaultColDef"
        :row-data="consultantsPage.results"
        row-selection="multiple"
        col-resize-default="shift"
        suppress-row-click-selection="true"
        :animate-rows="true"
        :pagination="true"
        :pagination-page-size="paginationPageSize"
        :suppress-pagination-panel="true"
        :enable-rtl="$vs.rtl"
        @grid-ready="onGridReady"
      />

      <vs-pagination
        v-model="currentPage"
        class="mt-6 md:mt-0"
        :total="totalPages"
        :max="7"
        icon-pack="fas"
        prev-icon="fa-chevron-left"
        next-icon="fa-chevron-right"
      />
      <div class="text-right">
        <router-link :to="{ name: 'consultant.create' }" class="btn"
          >Berater erstellen</router-link
        >
      </div>
    </div>
  </div>
</template>

<script>
import { agridMixin } from "@/mixins/agridMixin.js";
import { paginationMixin } from "@/mixins/paginationMixin.js";
import gql from "graphql-tag";
import CellRendererActions from "./cell-renderer/CellRendererActions";
import CellRendererActive from "./cell-renderer/CellRendererActive";
import DataInput from "@/components/ui/DataInput";
import projectState from "@/data/enums/ProjectState.js";

export default {
  components: {
    DataInput,

    // Cell renderer
    CellRendererActions, // eslint-disable-line
    CellRendererActive // eslint-disable-line
  },
  mixins: [paginationMixin("consultantsPage"), agridMixin],
  data() {
    return {
      // Filter Options
      filter: {
        consultant: null,
        company: null,
        userGroup: null,
        occupationCategory: null,
        jobType: null,
        state: null,
        projectStage: null,
        recruitment: null
      },
      projectState,
      columnDefs: [
        {
          headerName: "",
          checkboxSelection: true,
          headerCheckboxSelectionFilteredOnly: true,
          headerCheckboxSelection: true,
          width: 50,
          minWidth: 50
        },
        {
          headerName: "Vorname",
          field: "firstName",
          minWidth: 50
        },
        {
          headerName: "Nachname",
          field: "lastName",
          minWidth: 50
        },
        {
          headerName: "Abteilung",
          field: "department.name",
          minWidth: 50
        },
        {
          headerName: "Standort",
          field: "department.branchOffice",
          minWidth: 50
        },
        {
          headerName: "Telefonnummer",
          field: "telephone",
          minWidth: 50
        },
        {
          headerName: "Status",
          field: "active",
          width: 85,
          cellRendererFramework: "CellRendererActive"
        },
        {
          headerName: "",
          field: "transactions",
          width: 100,
          cellRendererFramework: "CellRendererActions"
        }
      ],
      components: {
        CellRendererActions,
        CellRendererActive
      }
    };
  },
  apollo: {
    consultantsPage: {
      query: gql`
        query Consultants($page: Int!, $size: Int, $filter: ConsultantFilter) {
          consultantsPage(page: $page, size: $size, filter: $filter) {
            total
            results {
              id
              salutation
              firstName
              lastName
              position
              department {
                id
                name
                branchOffice
              }
              telephone
              user {
                id
                active
              }
            }
          }
        }
      `,
      fetchPolicy: "network-only",
      variables() {
        return {
          page: this.currentPage,
          size: this.paginationPageSize,
          filter: {
            search: this.searchQuery
          }
        };
      }
    }
  }
};
</script>

<style lang="scss">
#page-user-list {
  .user-list-filters {
    .vs__actions {
      position: absolute;
      right: 0;
      top: 50%;
      transform: translateY(-58%);
    }
  }
}
</style>
